import { printCustomerName } from 'components/CustomerAdmin/utils'
import { useCustomerSellPopupContext } from 'components/Sell/CustomerSellPopup/CustomerSellPopupContext'
import usePosNameFormat from 'components/Sell/hook/usePosNameFormat'
import {
    displayCartItemName,
    displayItemName,
    getModOptionName,
    isComp,
} from 'components/Sell/utils/common'
import Decimal from 'decimal.js'
import { keys, min, sumBy, values } from 'lodash-es'
import {
    DiscountContract,
    DiscountType,
    OrderContract,
    OrderDetailContract,
    OrderLookupContract,
    OrderStatus,
    VoidCompReasonModel,
    VoidCompReasonType,
} from 'models'
import { useCallback } from 'react'
import {
    ComboViewModel,
    DiscountContractWithCustom,
    IOrderServiceChargeSetting,
    IServiceCharge,
    ItemMap,
    OrderItemViewModel,
    POSVariantGroupWithPrice,
    PriceListData,
    UsedModifiers,
    UsedVariants,
    usedModWithQuantity,
} from 'store/models/sellModels'
import { getItemPrice } from 'utilities/price/getPrice'
import {
    IDisplayOrderLine as OrderItemDisplay,
    IModifierDisplay,
    IDiscountDisplay,
    IVaraintDisplay,
} from 'views/CustomerFacingDisplay/components/OrderDisplay/interface'
import { useSaleDependenciesContext } from 'views/Sale/contexts/SaleDependenciesContext'
import {
    calculateDiscountAmount,
    calculateTotalEntireDiscount,
} from 'views/Sale/utils/orderCalculation/discountCalculation'
import {
    sumSubModifierQuantity,
    sumModifierQuantity,
    isDraftItem,
} from 'views/Sale/utils/orderItemUtil'
import {
    buildEntireOrderDiscounts,
    buildEntireVoidCompModel,
    useMockOrderLineToOrderItems,
} from 'components/Sell/OrderLookup/OrderInfo/buildOrderToOrderViewModel'
import { usePOSCFDHubContext } from 'hooks/usePOSHubContext/usePOSHubContext'
import {
    OrderMessageType,
    CFDClientMessageType,
    IOrderDisplay,
} from 'pages/CustomerFacingDisplayPage/reducer/Interface'
import { OrderViewModel } from 'views/Sale/contexts/OrdersContext'
import { usePosContext } from 'views/Sale/contexts/PosProvider'
import { ICalculationResultModel } from 'views/Sale/utils/orderCalculation/models/IOrderCalculationModel'
import { linesAndOrderCalculation } from 'views/Sale/utils/orderCalculation/linesAndOrderCalculation'
import calculateLine from 'views/Sale/utils/processLine'
function buildOrderDiscount(
    lineNetTotal: Decimal,
    order: OrderLookupContract,
    getDiscountName: (
        discount: Pick<
            DiscountContract,
            'type' | 'name' | 'value' | 'getAtDiscountType'
        >,
    ) => string,
    getCompName: (voidComp: VoidCompReasonModel) => string,
    getCompDescription: (voidComp: VoidCompReasonModel) => string,
    isCalculateCompAfterDiscount: boolean,
): {
    orderDiscounts: {
        name: string
        discountAmount: number
    }[]
    entireOrderVoidComp: VoidCompReasonModel | undefined
    orderComp: IDiscountDisplay | undefined
} {
    let totalAfterDiscount = lineNetTotal
    const entireOrderDiscounts = buildEntireOrderDiscounts(
        order?.orderDiscounts ?? [],
        [],
    )
    const entireOrderVoidComp = buildEntireVoidCompModel(order)
    const orderDiscounts =
        entireOrderDiscounts?.map((discount) => {
            const discountPrice = calculateDiscountAmount(
                discount,
                totalAfterDiscount,
            )
            const currentPrice = totalAfterDiscount.toNumber()
            totalAfterDiscount = totalAfterDiscount.sub(discountPrice)

            return {
                name: getDiscountName(discount),
                discountAmount:
                    min([discountPrice.toNumber(), currentPrice]) ??
                    discountPrice.toNumber(),
            }
        }) ?? []

    let orderComp: IDiscountDisplay | undefined
    if (entireOrderVoidComp && isComp(entireOrderVoidComp)) {
        orderComp = {
            name: getCompName(entireOrderVoidComp),
            reason: getCompDescription(entireOrderVoidComp),
            discountAmount: calculateTotalEntireDiscount(
                lineNetTotal,
                [],
                entireOrderVoidComp,
                isCalculateCompAfterDiscount,
            ).toNumber(),
        }
    }
    return { orderDiscounts, entireOrderVoidComp, orderComp }
}
function getSelectedVaraints(
    usedVariants: UsedVariants,
    variantGroups: POSVariantGroupWithPrice[],
    citemQuantity?: number,
) {
    const selectedVariants = values(usedVariants)
    return (
        variantGroups.flatMap<IVaraintDisplay>((vg) =>
            vg.variantOptions
                ?.filter((vo) =>
                    selectedVariants?.includes(vo.variantOptionID!),
                )
                ?.map((vo) => ({
                    name: vo.name!,
                    subTotal: citemQuantity
                        ? citemQuantity * vo.addPrice
                        : undefined,
                })),
        ) ?? []
    )
}
export const useSendOrderToCFD = () => {
    const { onSendMessageToCFS } = usePOSCFDHubContext()
    const mockOrderLine = useMockOrderLineToOrderItems()
    const {
        modifierOptionsMap,
        modifierList,
        priceListData,
        itemsMap,
        comboChoicesMap,
        discounts,
        cfdSetting,
    } = useSaleDependenciesContext()
    const {
        companySetting,
        isCalculateCompAfterDiscount,
        taxes,
        serviceChargeSetting,
    } = usePosContext()
    const { getCustomer } = useCustomerSellPopupContext()
    const {
        getDiscountName,
        getCompName,
        getCompDescription,
    } = usePosNameFormat()

    const getModDisplay = useCallback(
        (
            modWithQuantity: usedModWithQuantity[],
            bindingId: string,
            itemAmount: number,
            menuId?: string,
        ): IModifierDisplay[] => {
            return modWithQuantity.map<IModifierDisplay>((usedMod) => {
                const option = modifierOptionsMap[usedMod.modifierOptionId]

                const entityModifier = modifierList.find(
                    (a) => a.entityModifierId === bindingId,
                )

                const price = entityModifier?.hasAssociatedCost
                    ? usedMod.addPrice * usedMod.quantity * itemAmount
                    : undefined

                const subMods = keys(usedMod.subModifierOptions).flatMap<
                    IModifierDisplay
                >((bind) => {
                    const modWithQuantity = sumSubModifierQuantity(
                        usedMod,
                        bind,
                    )
                    return getModDisplay(
                        modWithQuantity,
                        bind,
                        itemAmount,
                        menuId,
                    )
                })

                return {
                    name: getModOptionName(option),
                    amount: usedMod.quantity,
                    subTotal: price,
                    subModifiers: subMods,
                }
            })
        },
        [modifierList, modifierOptionsMap],
    )
    const sendOrderToCFD = useCallback(
        (o: OrderMessageType) => {
            onSendMessageToCFS?.({
                type: CFDClientMessageType.Order,
                payload: o,
            })
        },
        [onSendMessageToCFS],
    )
    const getModifierOption = useCallback(
        (usedModifiers: UsedModifiers, itemAmount: number, menuId?: string) => {
            return keys(usedModifiers).flatMap<IModifierDisplay>(
                (bindingId) => {
                    const modWithQuantity = sumModifierQuantity(
                        usedModifiers[bindingId],
                    )
                    return getModDisplay(
                        modWithQuantity,
                        bindingId,
                        itemAmount,
                        menuId,
                    )
                },
            )
        },
        [getModDisplay],
    )
    const getComboItems = useCallback(
        (
            orderItem: OrderItemViewModel,
            itemQuantity: number,
            itemsMap: ItemMap,
            comboChoicesMap: Record<string, ComboViewModel[]>,
            itemId: string,
            menuId: string | undefined,
            priceListData: PriceListData,
        ) => {
            return keys(orderItem.usedItems).map<OrderItemDisplay>((path) => {
                const comboChoiceId = path.split(',')[0]!
                const usedItem = orderItem.usedItems[path]

                const citem = itemsMap[usedItem.nodeItemId]
                const currentComboItem = comboChoicesMap[itemId]
                    ?.find((a) => a.comboChoiceID === comboChoiceId)
                    ?.comboItems.find((a) => a.itemID === usedItem.nodeItemId)

                const price = getItemPrice(
                    currentComboItem?.comboChoiceItemId!,
                    menuId,
                    priceListData,
                )
                const comboItemSubtotal =
                    usedItem.quantity * itemQuantity * price
                const variantOptions = getSelectedVaraints(
                    usedItem.usedVariants,
                    currentComboItem?.variantGroups ?? [],
                    itemQuantity,
                )

                const modOptions = getModifierOption(
                    usedItem.usedModifiers,
                    itemQuantity,
                    orderItem.menuId,
                )

                return {
                    name: displayItemName(citem),
                    subTotal: comboItemSubtotal,
                    netTotal: comboItemSubtotal,
                    amount: usedItem.quantity,
                    variants: variantOptions,
                    modifies: modOptions,
                }
            })
        },
        [getModifierOption],
    )
    const resetOrderInCFD = useCallback(() => {
        if (!cfdSetting) return
        sendOrderToCFD({
            orderItems: [],
            orderDiscounts: [],
        })
    }, [cfdSetting, sendOrderToCFD])
    const getItemDisplay = useCallback(
        (
            orderItem: OrderItemViewModel,
            orderNameMap: Record<string, string> = {},
        ): OrderItemDisplay => {
            const menuId = orderItem.menuId
            const itemQuantity = orderItem.amount
            const itemId = orderItem.item.itemId!
            const variantOptions = getSelectedVaraints(
                orderItem.usedVariants,
                orderItem.item.variantGroups ?? [],
            )
            const modOptions = getModifierOption(
                orderItem.usedModifiers,
                orderItem.amount,
                orderItem.menuId,
            )
            const bPrice = getItemPrice(itemId, menuId, priceListData)
            const comboItems = getComboItems(
                orderItem,
                itemQuantity,
                itemsMap,
                comboChoicesMap,
                itemId,
                menuId,
                priceListData,
            )
            let dTotalLinePrice = new Decimal(
                orderItem.lineTotal * itemQuantity,
            )
            const discounts =
                orderItem.discounts?.map((discount) => {
                    //TODO: fix after pos-8962 merged
                    const discountPrice = calculateDiscountAmount(
                        discount,
                        discount.type === DiscountType.FixedAmount
                            ? dTotalLinePrice
                            : new Decimal(orderItem.lineTotal * itemQuantity),
                        itemQuantity,
                    )
                    const currentPrice = dTotalLinePrice.toNumber()
                    dTotalLinePrice = dTotalLinePrice.sub(discountPrice)

                    return {
                        name: getDiscountName(discount),
                        discountAmount:
                            min([discountPrice.toNumber(), currentPrice]) ??
                            discountPrice.toNumber(),
                    }
                }) ?? []

            let orderComp: IDiscountDisplay | undefined
            if (
                orderItem.voidComp &&
                orderItem.voidComp?.type === VoidCompReasonType.Comp
            ) {
                orderComp = {
                    name: getCompName(orderItem.voidComp),
                    reason: getCompDescription(orderItem.voidComp),
                    discountAmount: orderItem.totalCompDiscount ?? 0,
                }
            }

            return {
                name: displayCartItemName(orderItem),
                subOrderLines: comboItems,
                variants: variantOptions,
                modifies: modOptions,
                amount: itemQuantity,
                basePrice: bPrice,
                netTotal:
                    (orderItem.lineTotalWithItemDiscount ?? 0) -
                    (orderItem.totalCompDiscount ?? 0),
                subTotal: orderItem.lineTotal * itemQuantity,
                discounts,
                compDiscount: orderComp,
                void:
                    orderItem.voidComp &&
                    orderItem.voidComp?.type === VoidCompReasonType.Void
                        ? {
                              reason: orderItem.voidComp.name!,
                              note: orderItem.voidComp.note!,
                          }
                        : undefined,
                assignTo: orderItem.assignTo?.map((c) => orderNameMap[c]),
            }
        },
        [
            comboChoicesMap,
            getComboItems,
            getCompDescription,
            getCompName,
            getDiscountName,
            getModifierOption,
            itemsMap,
            priceListData,
        ],
    )
    const sendQSOrder = useCallback(
        (props: {
            orderTotalResult: ICalculationResultModel
            orderItems: OrderItemViewModel[]
            entireOrderDiscounts: DiscountContractWithCustom[]
            entireOrderVoidComp?: VoidCompReasonModel
            orderCustomerID?: string
            editingOrder?: OrderContract | OrderDetailContract | undefined
            orderServiceChargeSetting: IOrderServiceChargeSetting
        }) => {
            if (!cfdSetting) return
            const {
                orderItems,
                orderTotalResult,
                editingOrder,
                orderCustomerID,
                entireOrderDiscounts,
                entireOrderVoidComp,
                orderServiceChargeSetting,
            } = props
            const orderLineInfos = orderItems.map<OrderItemDisplay>((l) =>
                getItemDisplay(l),
            )
            let totalAfterDiscount = new Decimal(
                sumBy(orderLineInfos, (a) => a.netTotal ?? 0),
            )
            const orderDiscounts =
                entireOrderDiscounts?.map((discount) => {
                    const discountPrice = calculateDiscountAmount(
                        discount,
                        totalAfterDiscount,
                    )
                    const currentPrice = totalAfterDiscount.toNumber()
                    totalAfterDiscount = totalAfterDiscount.sub(discountPrice)

                    return {
                        name: getDiscountName(discount),
                        discountAmount:
                            min([discountPrice.toNumber(), currentPrice]) ??
                            discountPrice.toNumber(),
                    }
                }) ?? []

            let orderComp: IDiscountDisplay | undefined
            if (entireOrderVoidComp && isComp(entireOrderVoidComp)) {
                orderComp = {
                    name: getCompName(entireOrderVoidComp),
                    reason: getCompDescription(entireOrderVoidComp),
                    discountAmount: calculateTotalEntireDiscount(
                        new Decimal(
                            sumBy(orderLineInfos, (a) => a.netTotal ?? 0),
                        ),
                        [],
                        entireOrderVoidComp,
                        isCalculateCompAfterDiscount,
                    ).toNumber(),
                }
            }

            sendOrderToCFD({
                customerName: orderCustomerID
                    ? printCustomerName(getCustomer(orderCustomerID))
                    : undefined,
                orderName: editingOrder?.orderNo!,
                orderItems: orderLineInfos,
                orderDiscounts,
                orderVoid:
                    entireOrderVoidComp &&
                    entireOrderVoidComp.type === VoidCompReasonType.Void
                        ? {
                              reason: entireOrderVoidComp.name!,
                              note: entireOrderVoidComp.note!,
                          }
                        : undefined,
                orderComp,
                totalServiceCharge: orderTotalResult.totalServiceCharge,
                totalDiscount: orderTotalResult.totalDiscount,
                subtotal: orderTotalResult.subtotal,
                totalDue: orderTotalResult.totalDue,
                totalTax: orderTotalResult.totalTax,
                totalExclusiveTax: orderTotalResult.totalExclusiveTax,
                totalInclusiveTax: orderTotalResult.totalInclusiveTax,
                serviceChargeRate: orderServiceChargeSetting?.currentServiceChageRate!,
            })
        },
        [
            cfdSetting,
            getCompDescription,
            getCompName,
            getCustomer,
            getDiscountName,
            getItemDisplay,
            isCalculateCompAfterDiscount,
            sendOrderToCFD,
        ],
    )

    const sendOrderFromContract = useCallback(
        (order: OrderLookupContract) => {
            if (!cfdSetting) return
            const orderLineInfos = mockOrderLine(order.orderLines ?? [])
                .orderItems.filter((a) => !a.isSharedOrderLine)
                .map<OrderItemDisplay>((orderItem: OrderItemViewModel) => {
                    return getItemDisplay(orderItem)
                })

            const {
                orderDiscounts,
                entireOrderVoidComp,
                orderComp,
            } = buildOrderDiscount(
                new Decimal(sumBy(orderLineInfos, (a) => a.netTotal ?? 0)),
                order,
                getDiscountName,
                getCompName,
                getCompDescription,
                isCalculateCompAfterDiscount,
            )

            sendOrderToCFD({
                customerName: order.customerID
                    ? printCustomerName(getCustomer(order.customerID))
                    : undefined,
                tableGroupName: order.tableGroupName ?? '',
                tableName: order.tableName ?? '',
                orderName: order?.aliasName ?? order.orderNo ?? '',
                orderItems: orderLineInfos,
                orderDiscounts,
                orderVoid:
                    entireOrderVoidComp &&
                    entireOrderVoidComp.type === VoidCompReasonType.Void
                        ? {
                              reason: entireOrderVoidComp.name!,
                              note: entireOrderVoidComp.note!,
                          }
                        : undefined,
                orderComp,
                totalServiceCharge: order.serviceChargeAmount,
                totalDiscount: order.discountAmount,
                subtotal: order.subtotal,
                totalDue: order.totalDueAmount,
                totalTax: order.totalTaxAmount,
                totalExclusiveTax: order.taxExclusiveAmount,
                totalInclusiveTax: order.taxInclusiveAmount,
                serviceChargeRate: order?.serviceChargeRate!,
            })
        },
        [
            cfdSetting,
            getCompDescription,
            getCompName,
            getCustomer,
            getDiscountName,
            getItemDisplay,
            isCalculateCompAfterDiscount,
            mockOrderLine,
            sendOrderToCFD,
        ],
    )
    const sendTSOrder = useCallback(
        (draftOrderItems: OrderItemViewModel[], orders: OrderViewModel[]) => {
            if (!cfdSetting) return
            const orderDetails =
                orders?.filter((a) => a.status !== OrderStatus.Voided) ?? []

            let allLineInTables = orderDetails.flatMap((a) =>
                a.orderItems.filter(
                    (l) => !l.isSubSharedOrderLine && !isDraftItem(l),
                ),
            )
            allLineInTables = [...allLineInTables, ...draftOrderItems]
            const defaultBill = orderDetails.find((a) => a.isDefaultBill)
            if (!defaultBill) return

            const calculatedLines = allLineInTables.map((l) =>
                calculateLine(l, isCalculateCompAfterDiscount),
            )
            const orderNameMap = getOrderNameMap(orderDetails)
            const orderLineInfos = calculatedLines.map<OrderItemDisplay>(
                (orderItem: OrderItemViewModel) => {
                    return getItemDisplay(orderItem, orderNameMap)
                },
            )

            const orderSummaries = orderDetails
                .map<IOrderDisplay>((order) => {
                    const lineInOrders = calculatedLines
                        .filter((line) =>
                            line.assignTo?.includes(order.orderID!),
                        )
                        .map((relatedLine) => {
                            return (
                                relatedLine.subSharedLines?.find(
                                    (sub) =>
                                        sub.assignTo?.[0] === order.orderID,
                                ) ?? relatedLine
                            )
                        })

                    const {
                        orderDiscounts,
                        entireOrderVoidComp,
                        orderComp,
                    } = buildOrderDiscount(
                        new Decimal(
                            sumBy(
                                lineInOrders,
                                (a) => a.lineTotalWithItemDiscount ?? 0,
                            ),
                        ),
                        order,
                        getDiscountName,
                        getCompName,
                        getCompDescription,
                        isCalculateCompAfterDiscount,
                    )

                    const orderSC: IServiceCharge = {
                        orderServiceChageRate: order.serviceChargeRate ?? 0,
                        setting: serviceChargeSetting,
                        taxes,
                        isTaxInclusive: companySetting?.isTaxInclusive ?? false,
                    }
                    const { calculatedOrder } = linesAndOrderCalculation(
                        lineInOrders,
                        isCalculateCompAfterDiscount,
                        taxes,
                        modifierOptionsMap,
                        itemsMap,
                        orderSC,
                        buildEntireOrderDiscounts(
                            order.orderDiscounts ?? [],
                            discounts,
                        ),
                        entireOrderVoidComp,
                    )
                    return {
                        orderDiscounts,
                        entireOrderVoidComp,
                        orderComp,
                        orderVoid:
                            entireOrderVoidComp &&
                            entireOrderVoidComp.type === VoidCompReasonType.Void
                                ? {
                                      reason: entireOrderVoidComp.name!,
                                      note: entireOrderVoidComp.note!,
                                  }
                                : undefined,
                        orderNote: order.note ?? '',
                        customerName: order.customerID
                            ? printCustomerName(getCustomer(order.customerID))
                            : undefined,
                        orderName: order.aliasName ?? order.orderNo ?? '',
                        subtotal: calculatedOrder.subtotal ?? 0,
                        totalDiscount: calculatedOrder.totalDiscount ?? 0,
                        totalDue: calculatedOrder.totalDue ?? 0,
                        totalTax: calculatedOrder.totalTax ?? 0,
                        totalExclusiveTax:
                            calculatedOrder.totalExclusiveTax ?? 0,
                        totalInclusiveTax:
                            calculatedOrder.totalInclusiveTax ?? 0,
                        totalServiceCharge:
                            calculatedOrder.totalServiceCharge ?? 0,
                        serviceChargeRate: order.serviceChargeRate ?? 0,
                    }
                })
                .filter((a) => a.totalDue !== 0 && a.subtotal !== 0)

            sendOrderToCFD({
                customerName:
                    orderDetails.length === 1 && defaultBill.customerID
                        ? printCustomerName(getCustomer(defaultBill.customerID))
                        : '',
                tableGroupName: defaultBill?.tableGroupName ?? '',
                tableName: defaultBill?.tableName ?? '',
                orderName: defaultBill?.tableGroupID
                    ? 'Table Group Order'
                    : 'Table Order',
                orderItems: orderLineInfos,
                orderDiscounts: orderSummaries[0]?.orderDiscounts ?? [],
                orderVoid: orderSummaries[0]?.orderVoid,
                orderComp: orderSummaries[0]?.orderComp,
                totalServiceCharge: 0,
                subtotal: orderSummaries[0]?.subtotal ?? 0,
                totalDiscount: orderSummaries[0]?.totalDiscount ?? 0,
                totalDue: orderSummaries[0]?.totalDue ?? 0,
                totalTax: orderSummaries[0]?.totalTax ?? 0,
                totalExclusiveTax: orderSummaries[0]?.totalExclusiveTax ?? 0,
                totalInclusiveTax: orderSummaries[0]?.totalInclusiveTax ?? 0,
                serviceChargeRate: orderSummaries[0]?.totalServiceCharge ?? 0,

                multipleOrderDetails: orderSummaries,
            })
        },
        [
            cfdSetting,
            companySetting?.isTaxInclusive,
            discounts,
            getCompDescription,
            getCompName,
            getCustomer,
            getDiscountName,
            getItemDisplay,
            isCalculateCompAfterDiscount,
            itemsMap,
            modifierOptionsMap,
            sendOrderToCFD,
            serviceChargeSetting,
            taxes,
        ],
    )
    return {
        sendQSOrder,
        resetOrderInCFD,
        sendOrderFromContract,
        sendTSOrder,
    }
}

function getOrderNameMap(orderDetails: OrderViewModel[]) {
    const orderNameMap: Record<string, string> = {}
    orderDetails.forEach((o) => {
        orderNameMap[o.orderID!] = o.aliasName ?? ''
    })
    return orderNameMap
}
